// frontend/src/components/reports/ReportTemplate.js
// Shared wrapper for all report pages — handles loading, error, header and Excel export
import React from 'react';
import {
  Container, Box, Paper, Typography, Button,
  CircularProgress, Alert,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  FileDownload as ExportIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const ReportTemplate = ({
  title,
  subtitle,
  loading,
  error,
  onRefresh,
  // Excel export config
  exportFileName,
  exportData,       // array of row objects
  exportColumns,    // array of { key, label } — defines column order + headers
  // Optional summary chips rendered under the title
  summaryItems,     // array of { label, value, color }
  children,         // the report table/content
}) => {
  const navigate = useNavigate();

  const handleExport = () => {
    if (!exportData || exportData.length === 0) return;

    // Build rows using exportColumns order
    const sheetData = exportData.map((row) => {
      const out = {};
      for (const col of exportColumns) {
        let val = row[col.key];
        // Format dates
        if (val instanceof Date) val = val.toLocaleString('en-GB');
        else if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}T/)) {
          val = new Date(val).toLocaleString('en-GB');
        }
        out[col.label] = val ?? '-';
      }
      return out;
    });

    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Report');

    // Auto-width columns
    const colWidths = exportColumns.map((col) => ({
      wch: Math.max(
        col.label.length,
        ...sheetData.map((r) => String(r[col.label] ?? '').length)
      ) + 2,
    }));
    ws['!cols'] = colWidths;

    XLSX.writeFile(wb, `${exportFileName}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 6 }}>

        {/* Header */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
            <Box>
              <Box
                onClick={() => navigate('/reports')}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: 'primary.main', mb: 1, '&:hover': { textDecoration: 'underline' } }}
              >
                <BackIcon fontSize="small" />
                <Typography variant="caption">Back to Reports</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold">{title}</Typography>
              {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{subtitle}</Typography>}

              {/* Summary chips */}
              {summaryItems && summaryItems.length > 0 && (
                <Box display="flex" gap={2} flexWrap="wrap" sx={{ mt: 2 }}>
                  {summaryItems.map((item) => (
                    <Box key={item.label} sx={{ textAlign: 'center', px: 2, py: 1, bgcolor: item.bg || 'grey.100', borderRadius: 2, minWidth: 80 }}>
                      <Typography variant="h6" fontWeight="bold" color={item.color || 'text.primary'}>{item.value}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Action buttons */}
            <Box display="flex" gap={1.5} alignItems="flex-start">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={onRefresh}
                disabled={loading}
                size="small"
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<ExportIcon />}
                onClick={handleExport}
                disabled={loading || !exportData || exportData.length === 0}
                size="small"
                sx={{ fontWeight: 'bold' }}
              >
                Export to Excel
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
        )}

        {/* Loading */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={10}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          children
        )}

      </Box>
    </Container>
  );
};

export default ReportTemplate;